<template>
  <b-card>
    <div
      class="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom"
    >
      <h5 class="font-weight-bolder mb-0">Terms &amp; Conditions</h5>
      <b-button variant="primary" size="sm" @click="openModal(null)">
        <feather-icon icon="PlusIcon" class="mr-50" />
        Add Template
      </b-button>
    </div>

    <div v-if="loading" class="text-center py-3">
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="!list || list.length === 0"
      class="text-center text-muted py-3"
    >
      No terms &amp; conditions templates found.
    </div>

    <div v-else>
      <b-form-group label="Choose Template" label-for="tc-select">
        <div class="d-flex align-items-center" style="gap: 8px">
          <b-form-select
            id="tc-select"
            v-model="selectedId"
            :options="templateOptions"
            class="flex-grow-1"
          />
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="!selectedItem"
            @click="openModal(selectedItem)"
          >
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="deleting || !selectedItem"
            @click="handleDelete(selectedItem)"
          >
            <feather-icon icon="TrashIcon" />
          </b-button>
        </div>
      </b-form-group>

      <b-form-group
        v-if="selectedItem"
        label="Terms Content"
        label-for="tc-content-preview"
        class="mt-1"
      >
        <b-form-textarea
          id="tc-content-preview"
          :value="selectedItem.content"
          rows="6"
          readonly
          class="bg-light"
        />
        <small class="text-muted">You can edit the template text before publishing</small>
      </b-form-group>
    </div>

    <!-- Create / Edit Modal -->
    <b-modal
      id="term-condition-modal"
      :title="editItem ? 'Edit Template' : 'Add Template'"
      ok-title="Save"
      cancel-variant="outline-secondary"
      :ok-disabled="saving"
      @ok.prevent="handleSave"
      @hidden="resetForm"
      centered
    >
      <b-form>
        <b-form-group label="Template Name" label-for="tc-name">
          <b-form-input
            id="tc-name"
            v-model="form.name"
            placeholder="e.g. Generic Price Promotion"
          />
        </b-form-group>
        <b-form-group label="Terms Content" label-for="tc-content">
          <b-form-textarea
            id="tc-content"
            v-model="form.content"
            placeholder="Enter terms content..."
            rows="6"
          />
          <small class="text-muted">You can edit the template text before publishing</small>
        </b-form-group>
      </b-form>
      <template #modal-ok>
        <b-spinner v-if="saving" small class="mr-50" />
        <feather-icon v-else icon="SaveIcon" class="mr-50" />
        Save
      </template>
    </b-modal>

    <!-- Delete Confirm Modal -->
    <b-modal
      id="term-condition-delete-modal"
      title="Confirm Delete"
      ok-variant="danger"
      ok-title="Delete"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      @ok="confirmDelete"
    >
      <p class="text-black">
        Delete template <strong>"{{ deleteItem && deleteItem.name }}"</strong>?
        This cannot be undone.
      </p>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BButton,
    BSpinner,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
  },
  data() {
    return {
      selectedId: null,
      editItem: null,
      deleteItem: null,
      form: {
        name: "",
        content: "",
      },
    };
  },
  computed: {
    ...mapState("settings/term-conditions", [
      "loading",
      "saving",
      "deleting",
      "list",
    ]),
    templateOptions() {
      return (this.list || []).map((item) => ({
        value: item.id,
        text: item.name,
      }));
    },
    selectedItem() {
      if (!this.selectedId || !this.list) return null;
      return this.list.find((item) => item.id === this.selectedId) || null;
    },
  },
  watch: {
    list(val) {
      if (val && val.length > 0 && !this.selectedId) {
        this.selectedId = val[0].id;
      }
    },
  },
  created() {
    this.getTermConditions();
  },
  methods: {
    ...mapActions("settings/term-conditions", [
      "getTermConditions",
      "createTermCondition",
      "updateTermCondition",
      "deleteTermCondition",
    ]),
    openModal(item) {
      this.editItem = item;
      if (item) {
        this.form.name = item.name || "";
        this.form.content = item.content || "";
      }
      this.$bvModal.show("term-condition-modal");
    },
    resetForm() {
      this.editItem = null;
      this.form.name = "";
      this.form.content = "";
    },
    async handleSave() {
      if (!this.form.name.trim() || !this.form.content.trim()) return;
      const isEdit = !!this.editItem;
      const result = isEdit
        ? await this.updateTermCondition({ id: this.editItem.id, ...this.form })
        : await this.createTermCondition(this.form);
      if (result) {
        this.showToastSuccess("Saved", isEdit ? "Template updated." : "Template created.");
        this.$bvModal.hide("term-condition-modal");
      }
    },
    handleDelete(item) {
      this.deleteItem = item;
      this.$bvModal.show("term-condition-delete-modal");
    },
    async confirmDelete() {
      if (!this.deleteItem) return;
      const deletedId = this.deleteItem.id;
      const result = await this.deleteTermCondition(deletedId);
      if (result !== undefined) {
        this.showToastSuccess("Deleted", "Template deleted.");
        if (this.list && this.list.length > 0) {
          this.selectedId = this.list[0].id;
        } else {
          this.selectedId = null;
        }
      }
      this.deleteItem = null;
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>
